import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const LaunchPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [showDemo, setShowDemo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const sections = useMemo(
    () => [
      { id: 'modules', label: 'Modules' },
      { id: 'features', label: 'Features' },
      { id: 'resources', label: 'Resources' },
      { id: 'releases', label: 'Releases' },
      { id: 'contact', label: 'Contact' },
      { id: 'support', label: 'Support & FAQs' },
    ],
    []
  );

  const handleScrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      let current = '';
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 120 && rect.bottom >= 120) {
          current = s.id;
          break;
        }
      }
      setActiveSection(current);
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate email signup
    setTimeout(() => {
      setIsSubmitting(false);
      alert('Thank you! We\'ll send you exclusive launch updates and early access.');
      setEmail('');
    }, 1000);
  };

  const handleDemoRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setShowDemo(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-100 scroll-smooth">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <button
            className="md:hidden absolute left-2 top-2 z-[60] p-2 rounded-md bg-white/90 shadow hover:bg-white"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
          <div className="flex justify-between items-center py-3 md:py-4">
            <div className="flex items-center gap-2 md:gap-3 pl-10 md:pl-0">
              <div className="w-6 h-6 md:w-8 md:h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
              </div>
              <div>
                <div className="text-lg md:text-xl font-bold text-gray-900">CosmaComply</div>
                <div className="text-[10px] md:text-xs text-gray-500">Release 1</div>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleScrollTo(s.id)}
                  className={`transition-colors border-b-2 pb-1 ${
                    activeSection === s.id
                      ? 'text-indigo-700 border-indigo-600'
                      : 'text-gray-700 hover:text-indigo-600 border-transparent'
                  }`}
                  aria-current={activeSection === s.id ? 'page' : undefined}
                >
                  {s.label}
                </button>
              ))}
              <Link to="/login" className="text-gray-700 hover:text-indigo-600">Sign in</Link>
              <Link 
                to="/signup"
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                Schedule Demo
              </Link>
            </div>
            
          </div>
          <div className={`${mobileOpen ? 'block' : 'hidden'} md:hidden border-t border-gray-200 pb-3`}>
            <div className="py-2 space-y-1">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { handleScrollTo(s.id); setMobileOpen(false); }}
                  className={`block w-full text-left px-2 py-2 transition-colors ${
                    activeSection === s.id ? 'text-indigo-700' : 'text-gray-700 hover:text-indigo-600'
                  }`}
                >
                  {s.label}
                </button>
              ))}
              <div className="flex items-center gap-3 px-2 pt-2">
                <Link to="/login" className="text-gray-700 hover:text-indigo-600">Sign in</Link>
                <Link to="/signup" className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium">
                  Schedule Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Secondary sticky sub-navigation */}
      <div className="hidden md:block sticky top-14 z-40 bg-white/70 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 py-2 text-sm">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => handleScrollTo(s.id)}
                className={`px-2 py-1 rounded ${
                  activeSection === s.id
                    ? 'text-indigo-700 bg-indigo-50'
                    : 'text-gray-700 hover:text-indigo-700 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
              🚀 LAUNCHING THIS WEEK - Limited Early Access Available
            </span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
            Streamline MoCRA Compliance with AI-Assisted Workflows
          </h1>
          
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            An AI-assisted platform that helps streamline MoCRA workflows, track deadlines, and reduce risk of penalties.
            Built for cosmetic manufacturers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button 
              onClick={() => handleScrollTo('modules')}
              className="bg-indigo-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-indigo-700 transition-colors text-center shadow-lg hover:shadow-xl"
            >
              🎯 Explore Modules
            </button>
            <button 
              onClick={() => setShowDemo(true)}
              className="border-2 border-indigo-600 text-indigo-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-indigo-50 transition-colors"
            >
              📺 Watch Demo
            </button>
          </div>
          
          
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="scroll-mt-24 py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h3 className="text-3xl font-bold text-gray-900 mb-3">Contact Us</h3>
            <p className="text-gray-700">Questions or want a walkthrough? We’re here to help.</p>
          </div>
          <div className="flex justify-center gap-3">
            <a href="mailto:cosmacomply@gmail.com" className="border border-gray-300 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-100">Email us</a>
            <Link to="/signup" className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700">Schedule Demo</Link>
          </div>
        </div>
      </section>

      {/* Back to top floating button */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 h-11 w-11 rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          aria-label="Back to top"
        >
          ↑
        </button>
      )}

      {/* Social Proof Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Trusted by Industry Leaders
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">MoCRA-aligned Workflows</h3>
              <p className="text-gray-600">Built to support FDA MoCRA compliance tasks. Not FDA-approved.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Enterprise Security</h3>
              <p className="text-gray-600">Industry-standard encryption (in transit & at rest) and data isolation</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Lightning Fast</h3>
              <p className="text-gray-600">Set up in minutes, not months</p>
            </div>
          </div>

          {/* Optional: logos strip (hidden until assets available) */}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="scroll-mt-24 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for MoCRA Compliance
            </h2>
            <p className="text-xl text-gray-600">
              From facility registration to adverse event reporting, we've got you covered
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🏢</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Facility Registration</h3>
              <p className="text-gray-600">
                Guided FDA registration workflows with small business exemption support and renewal reminders.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">SAE Detection</h3>
              <p className="text-gray-600">
                AI-assisted severe adverse event detection with 15-day FDA deadline tracking to help reduce risk of penalties.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📋</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Product Management</h3>
              <p className="text-gray-600">
                Complete ingredient tracking and safety documentation management. 
                Centralized compliance for your entire product portfolio.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Assistant</h3>
              <p className="text-gray-600">
                Get instant answers to compliance questions. AI-powered guidance 
                tailored to your specific situation.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics Dashboard</h3>
              <p className="text-gray-600">
                Real-time compliance scoring and risk assessment. Executive-level 
                insights for strategic decision making.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📝</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Audit Trail</h3>
              <p className="text-gray-600">
                Complete audit logging for every action. Maintain regulatory 
                compliance with comprehensive documentation.
              </p>
            </div>
          </div>

          {/* Modules Grid */}
          <div id="modules" className="scroll-mt-24 mt-16">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Integrated Modules</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                'Facilities','Products','Adverse Events','Safety Documents','Workflow','GMP Compliance','Regulatory Submissions','Enterprise','User Management','AI Assistant','Aggregate Reporting','Labels'
              ].map((m)=> (
                <div key={m} className="bg-white border border-gray-200 rounded-lg px-4 py-5 text-sm font-medium text-gray-800 hover:shadow-sm flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                  {m}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">
            How CosmaComply Helps
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg">
              <div className="text-xl text-gray-900 mb-2 font-semibold">Faster Workflows</div>
              <div className="text-gray-600">Centralized modules, templates, and automation to speed up routine tasks.</div>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <div className="text-xl text-gray-900 mb-2 font-semibold">Lower Compliance Ops Cost</div>
              <div className="text-gray-600">Reduce manual effort with tracking, reminders, and smart validation.</div>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <div className="text-xl text-gray-900 mb-2 font-semibold">Higher Data Consistency</div>
              <div className="text-gray-600">AI-assisted checks and audit trails to support consistent, high-quality records.</div>
            </div>
          </div>
        </div>
      </section>

      

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-4">
            🚀 Ready to Transform Your Compliance?
          </h2>
          
          
          {/* Email Signup Form */}
          <form onSubmit={handleEmailSignup} className="max-w-md mx-auto mb-8">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Get Launch Updates'}
              </button>
            </div>
          </form>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="bg-indigo-600 text-white px-8 py-4 rounded-lg hover:bg-indigo-700 transition-colors font-semibold text-lg"
            >
              🎯 Get Early Access Now
            </Link>
          </div>
          
          
        </div>
      </section>

      {/* Releases & Support Section */}
      <section id="releases" className="scroll-mt-24 py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 border border-gray-200 rounded-xl bg-gray-50">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Releases</h3>
              <p className="text-gray-600 mb-4">Current: Release 1 — foundation modules, reporting, and GMP.
              </p>
              <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                <li>Professional UI across all modules</li>
                <li>XML/PDF generation and bulk submissions</li>
                <li>Audit trail and GMP dashboards</li>
              </ul>
            </div>
            <div className="p-8 border border-gray-200 rounded-xl bg-gray-50">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Support & FAQs</h3>
              <p className="text-gray-600 mb-4">Self-serve guides, onboarding materials, and responsive support.</p>
              <div className="flex gap-3">
                <Link to="/login" className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700">Sign in</Link>
                <Link to="/signup" className="border border-indigo-600 text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50">Get started</Link>
                <a href="mailto:cosmacomply@gmail.com" className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100">Email us</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted & Proven Technology */}
      <section id="resources" className="scroll-mt-24 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Trusted and Proven Technology</h3>
              <p className="text-gray-600 mb-4">Used by diverse organizations, from small cosmetics brands to enterprise manufacturers. Built with modern cloud and security best practices.</p>
              <ul className="text-gray-700 space-y-2 list-disc pl-5">
                <li>Secure multi-tenant architecture</li>
                <li>Scalable APIs and reporting engine</li>
                <li>Full audit logging and compliance tracking</li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-20 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm">ISO 27001</div>
              <div className="h-20 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm">SOC 2 Ready</div>
              <div className="h-20 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm">GxP</div>
              <div className="h-20 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm">SLA 99.9%</div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Demo CTA */}
      <section id="support" className="scroll-mt-24 py-16 bg-indigo-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-gray-900 mb-3">See CosmaComply in Action</h3>
          <p className="text-gray-700 mb-6">Book a 30-minute walkthrough of key modules and reporting. Get tailored guidance for your use cases.</p>
          <div className="flex justify-center gap-3">
            <Link to="/signup" className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700">Schedule Demo</Link>
            <Link to="/login" className="border border-indigo-600 text-indigo-700 px-6 py-3 rounded-lg hover:bg-indigo-100">Explore App</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold text-white mb-4">CosmaComply</div>
              <p className="text-gray-400">
                The leading MoCRA compliance platform for cosmetic manufacturers.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                
                <li><Link to="/api" className="hover:text-white">API</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/#about" className="hover:text-white">About</a></li>
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><a href="#contact" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                <li><Link to="/disclaimer" className="hover:text-white">Disclaimer & Security</Link></li>
                <li><a href="mailto:cosmacomply@gmail.com" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} CosmaComply. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Request Demo</h3>
            <form onSubmit={handleDemoRequest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  Request Demo
                </button>
                <button
                  type="button"
                  onClick={() => setShowDemo(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaunchPage;



























